<template>
  <BaseScreen
    :loading="isLoading"
    :showFooter="!hasDataToShow"
  >
    <Loader v-if="isLoading" />

    <FormBuilder
      v-else-if="hasDataToShow"
      class="form-builder"
      :data="formData"
      :form-states="formStates"
      :case_id="caseData.case_id"
      :app="currentApp"
      :save-section-method="saveSectionMethod"
      hide-single-section
      @submit="submitForm"
    >
      <template #navigation="slotProps">
        <FormBuilderNavBar
          v-bind="slotProps"
          :show-back-button="isRedirectionScreenBackButtonShown"
          @back="$emit('back')"
        />
      </template>
    </FormBuilder>

    <Empty
      v-else
      :text="$t('components.redirection_screen.there_is_no_data_to_display')"
      classList="text-lg"
      :showBtn="false"
      class="h-full"
    />
  </BaseScreen>
</template>

<script>
import axios from "@/axios";
import BaseScreen from "../components/BaseScreen.vue";
import FormBuilder from "@shared/components/form-builder";
import FormBuilderNavBar from "@shared/case-creation/CaseCreationFlow/components/FormBuilderNavBar"
import Loader from "@shared/components/WrappedLoader";
import Empty from "@shared/empty";
import handleError from "@shared/mixins/handleError.js";
import { appList } from "@shared/components/form-builder/utils/index.js";
import FormBuilderAPIUrlList from "@shared/components/form-builder/utils/api";
import {
  formatDataForFormPreview,
  formatSections,
} from "@shared/case-entities/utils";

export default {
  name: "Form",

  components: {
    BaseScreen,
    FormBuilder,
    FormBuilderNavBar,
    Loader,
    Empty,
  },

  mixins: [handleError],

  props: {
    formId: {
      type: String,
      required: true,
    },
  },

  inject: ["sharedCaseState", "currentApp"],

  data() {
    return {
      isLoading: false,
      formData: null,
      API: null,
      formStates: {
        loading: false,
        submitted: false,
      },
      customApi: {
        fetchForm: (payload) =>
          `case/${payload.case_id}/entity/${payload.entity_id}/additional-forms/${payload.form_id}`,
        saveSection: (payload) =>
          `case/${payload.case_id}/entity/${payload.entity_id}/additional-forms/${payload.form_id}`,
        submitForm: (payload) =>
        `/case${ this.currentApp == appList.CLIENT ? '/client' : ''}/${payload.case_id}/entity/${payload.entity_id}/form/${payload.form_id}/submit`
      },
      error: null,
    }
  },

  created() {
    const { case_id, selected_entity_id: entity_id } = this.caseData;
    this.API = new FormBuilderAPIUrlList(
      {
        case_id,
        entity_id,
        form_id: this.formId,
      },
      this.customApi
    );
  },

  async mounted() {
    this.isLoading = true;
    try {
      const { data } = await axios.get(this.API.fetchForm);
      this.formData = formatDataForFormPreview(data);
    } catch (error) {
      this.onError(error);
    } finally {
      this.isLoading = false;
    }
  },

  computed: {
    caseData() {
      return this.sharedCaseState();
    },
    isCaseSubmitted() {
      return this.caseData.is_case_submitted;
    },
    currentWorkflowObjectId() {
      return this.caseData.current_workflow_object_id;
    },
    isRedirectionScreenBackButtonShown() {
      return this.caseData.is_redirection_screen_back_button_shown ?? true;
    },
    disableSave() {
      return false;
    },
    hasDataToShow() {
      return this.formData?.sections?.length > 0;
    }
  },

  methods: {
    onError(error) {
      this.handleError(error);
      this.error = error;
    },

    async saveSectionMethod(payload) {
      this.formStates.loading = true;
      try {
        await axios.post(this.API.saveSection, formatSections(payload));
      } catch (error) {
        this.onError(error);
      } finally {
        this.formStates.loading = false;
      }
    },

    async submitForm() {
      this.formStates.loading = true;

      try {
        await axios.post(this.API.submitForm);
        await this.submitRedirectionForm();
        this.$emit("submit");
      } catch (error) {
        this.onError(error);
      } finally {
        this.formStates.loading = false;
      }
    },

    async submitRedirectionForm() {
      const payload = { case_additional_form_id: this.formId };
      const objectKey = this.isCaseSubmitted ? "case_id" : "case_entity_id";
      payload[objectKey] = this.currentWorkflowObjectId;
      await axios.post(`case/ui-redirection-form-submitted`, payload);
    },
  }
}
</script>

<style lang="scss" scoped>
.form-builder {
  background-color: white !important;
}
</style>
